import React, { useState, useEffect } from 'react';
import { Brain, Save, RotateCcw, CheckCircle, AlertCircle, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useToast } from '../hooks/use-toast';
import api from '../lib/api';
import { NavbarDemo } from './NavBar';

const DEFAULT_TRAINING_PROMPT = `You are a helpful, professional booking assistant for BookaTherapy - a platform that connects clients with professional massage therapists and wellness practitioners.

YOUR ROLE:
- Answer questions about BookaTherapy and our services
- Guide clients through the booking process
- Provide helpful information about therapy types, pricing, and booking options
- Be friendly, patient, and informative

ABOUT BOOKATHERAPY:
- We connect clients with licensed massage therapists and wellness practitioners
- Services available: Massage therapy (Swedish, deep tissue, sports, prenatal, etc.)
- Booking options: In-clinic appointments, mobile (therapist comes to you), or home-based services
- Payment: Secure online payment via Stripe or pay at clinic
- Coverage: Multiple cities across Canada (ask for their city to check availability)
- All therapists are licensed, background-checked professionals

CONVERSATION APPROACH:
- Keep responses concise but informative (1-3 sentences)
- Answer questions directly - don't redirect to support unless truly necessary
- When helping with booking, ask ONE question at a time
- Be conversational and warm, not robotic

BOOKING FLOW (when user wants to book):
1. Ask what brings them in (e.g., stress relief, pain management, relaxation)
2. Ask for their city/location
3. Ask about service preference (clinic, mobile, or home-based)
4. Ask about therapist gender preference (if any)
5. Ask about preferred date and time
6. Collect contact info (name, email, phone)
7. Guide them to payment/confirmation

IMPORTANT:
- ALWAYS try to answer questions - you have knowledge about BookaTherapy services
- Only say you can't help if the question is truly outside your scope (medical advice, technical issues, etc.)
- Be helpful and informative - your goal is to make booking easy and answer questions
- Never leave users hanging - always provide a helpful response or next step`;

export default function ChatBotTraining() {
  const [trainingPrompt, setTrainingPrompt] = useState(DEFAULT_TRAINING_PROMPT);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadTrainingPrompt();
  }, []);

  const loadTrainingPrompt = async () => {
    try {
      // Try to get from backend first
      const data = await api.chatbot.getTraining();
      if (data?.prompt) {
        setTrainingPrompt(data.prompt);
        setLastSaved(data.updatedAt || new Date().toISOString());
      }
    } catch (error) {
      console.error('Error loading training prompt:', error);
      // Fallback to localStorage
      const stored = localStorage.getItem('bookatherapy_chatbot_training');
      if (stored) {
        setTrainingPrompt(stored);
      }
      const lastSavedTime = localStorage.getItem('bookatherapy_chatbot_training_time');
      if (lastSavedTime) {
        setLastSaved(lastSavedTime);
      }
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Try to save to backend
      const response = await api.chatbot.updateTraining(trainingPrompt);

      setLastSaved(response.updatedAt || new Date().toISOString());
      setHasChanges(false);

      toast({
        title: 'Success!',
        description: 'AI training prompt saved successfully.',
      });
    } catch (error) {
      console.error('Error saving training prompt:', error);

      // Fallback to localStorage if backend fails
      localStorage.setItem('bookatherapy_chatbot_training', trainingPrompt);
      const now = new Date().toISOString();
      localStorage.setItem('bookatherapy_chatbot_training_time', now);

      setLastSaved(now);
      setHasChanges(false);

      toast({
        title: 'Saved Locally',
        description: 'Training prompt saved to browser storage.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset to default training prompt?')) {
      setTrainingPrompt(DEFAULT_TRAINING_PROMPT);
      setHasChanges(true);
      toast({
        title: 'Reset',
        description: 'Training prompt reset to default. Click Save to apply.',
      });
    }
  };

  const handleChange = (e) => {
    setTrainingPrompt(e.target.value);
    setHasChanges(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      <NavbarDemo />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 mt-20">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-14 h-14 bg-gradient-to-br from-[#252F4F] to-[#1e2640] rounded-2xl flex items-center justify-center">
              <Brain className="h-7 w-7 text-[#98C454]" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-[#252F4F]">AI Chatbot Training</h1>
              <p className="text-gray-600 mt-1">Configure how your AI assistant responds to clients</p>
            </div>
          </div>

          {/* Status Indicators */}
          <div className="flex flex-wrap items-center gap-4 mt-6">
            <Badge className="bg-[#98C454] text-[#252F4F] px-4 py-2">
              <Sparkles className="w-4 h-4 mr-2" />
              Powered by OpenAI
            </Badge>

            {hasChanges && (
              <Badge variant="outline" className="border-orange-500 text-orange-600 px-4 py-2">
                <AlertCircle className="w-4 h-4 mr-2" />
                Unsaved Changes
              </Badge>
            )}

            {lastSaved && !hasChanges && (
              <Badge variant="outline" className="border-green-500 text-green-600 px-4 py-2">
                <CheckCircle className="w-4 h-4 mr-2" />
                Last saved: {new Date(lastSaved).toLocaleString()}
              </Badge>
            )}
          </div>
        </div>

        {/* Training Prompt Editor */}
        <div className="bg-white rounded-2xl border-2 border-gray-200 shadow-xl overflow-hidden">
          {/* Editor Header */}
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-6 border-b border-gray-200">
            <h2 className="text-xl font-bold text-[#252F4F] mb-2">Training Prompt</h2>
            <p className="text-sm text-gray-600">
              This prompt defines your AI assistant's personality, rules, and conversation flow.
              Make sure to keep responses under 10 words for best results.
            </p>
          </div>

          {/* Text Area */}
          <div className="p-6">
            <textarea
              value={trainingPrompt}
              onChange={handleChange}
              className="w-full h-[500px] p-6 font-mono text-sm border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#98C454] focus:border-transparent resize-none bg-gray-50"
              placeholder="Enter your AI training prompt here..."
            />

            {/* Character Count */}
            <div className="mt-4 flex justify-between items-center">
              <p className="text-sm text-gray-500">
                {trainingPrompt.length} characters • {trainingPrompt.split('\n').length} lines
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            onClick={handleSave}
            disabled={isSaving || !hasChanges}
            size="lg"
            className="bg-[#98C454] hover:bg-[#87b043] text-[#252F4F] px-10 py-6 text-lg rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 font-semibold disabled:opacity-50"
          >
            {isSaving ? (
              <>
                <RotateCcw className="mr-2 h-5 w-5 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-5 w-5" />
                Save Training Prompt
              </>
            )}
          </Button>

          <Button
            onClick={handleReset}
            variant="outline"
            size="lg"
            className="border-2 border-[#252F4F] text-[#252F4F] hover:bg-[#252F4F] hover:text-white px-10 py-6 text-lg rounded-xl font-semibold transition-all duration-300"
          >
            <RotateCcw className="mr-2 h-5 w-5" />
            Reset to Default
          </Button>
        </div>

        {/* Guidelines Card */}
        <div className="mt-12 bg-gradient-to-br from-blue-50 to-white border-2 border-blue-200 rounded-2xl p-8">
          <h3 className="text-2xl font-bold text-[#252F4F] mb-4 flex items-center gap-2">
            <Brain className="h-6 w-6 text-[#98C454]" />
            Best Practices
          </h3>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-bold text-[#252F4F] mb-2">✓ DO</h4>
              <ul className="space-y-2 text-sm text-gray-700">
                <li>• Keep responses under 10 words</li>
                <li>• Ask one question at a time</li>
                <li>• Be calm, kind, and professional</li>
                <li>• Focus on collecting information</li>
                <li>• Use simple, clear language</li>
                <li>• Provide conversation flow examples</li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold text-red-600 mb-2">✗ DON'T</h4>
              <ul className="space-y-2 text-sm text-gray-700">
                <li>• Use emojis in responses</li>
                <li>• Give sales pitches</li>
                <li>• Repeat or explain policies</li>
                <li>• Ask multiple questions at once</li>
                <li>• Use overly technical language</li>
                <li>• Make assumptions without asking</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Example Conversations */}
        <div className="mt-8 bg-white border-2 border-gray-200 rounded-2xl p-8">
          <h3 className="text-2xl font-bold text-[#252F4F] mb-6">Example Conversation Flow</h3>

          <div className="space-y-4">
            {[
              { user: 'Hi', bot: 'Hello! How can I help you today?' },
              { user: 'I need a massage', bot: 'Sure. What city are you in?' },
              { user: 'Edmonton', bot: 'Great. Clinic or mobile service?' },
              { user: 'Clinic', bot: 'Which therapy are you interested in?' },
              { user: 'Deep tissue for back pain', bot: 'Got it. Male or female therapist?' },
              { user: 'Female therapist', bot: 'Okay. What day and time works?' },
            ].map((msg, idx) => (
              <div key={idx} className="flex flex-col gap-2">
                <div className="flex justify-end">
                  <div className="bg-[#98C454] text-[#252F4F] px-4 py-2 rounded-2xl rounded-br-sm max-w-xs">
                    <p className="text-sm font-medium">{msg.user}</p>
                  </div>
                </div>
                <div className="flex justify-start">
                  <div className="bg-gray-100 text-gray-800 px-4 py-2 rounded-2xl rounded-bl-sm max-w-xs border border-gray-200">
                    <p className="text-sm">{msg.bot}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
